import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  deleteRequestExpensesCategory,
  updateRequestExpensesCategory,
} from 'lib/api/requestExpenses';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import KeyValueRow from 'lib/designSystem/KeyValueRow';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { RequestExpensesCategory } from '..';

type RequestExpensesCategoryProps = {
  category: RequestExpensesCategory | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  setUp: () => Promise<void>;
};

const RequestExpensesCategorySummary = (
  props: RequestExpensesCategoryProps,
) => {
  const { category, open, setOpen, setUp } = props;
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    hasIva: false,
  });
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        hasIva: category.hasIva,
      });
    }
  }, [category]);

  const handleSave = async () => {
    if (!category) return;

    if (!formData.name.trim()) {
      showAlert(dispatch, 'error', 'El nombre es requerido');
      return;
    }

    setLoading(true);
    try {
      await updateRequestExpensesCategory(dispatch, category.id, formData);
      showAlert(dispatch, 'success', 'Categoría actualizada exitosamente');
      setEditing(false);
      setOpen(false);
      await setUp();
    } catch (error) {
      console.error('Error updating category:', error);
      showAlert(dispatch, 'error', 'Error al actualizar la categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!category) return;

    setLoading(true);
    try {
      await deleteRequestExpensesCategory(dispatch, category.id);
      showAlert(dispatch, 'success', 'Categoría eliminada exitosamente');
      setOpenConfirmDelete(false);
      setOpen(false);
      await setUp();
    } catch (error) {
      console.error('Error deleting category:', error);
      showAlert(dispatch, 'error', 'Error al eliminar la categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (category) {
      setFormData({
        name: category.name,
        hasIva: category.hasIva,
      });
    }
    setEditing(false);
  };

  if (!category) return null;

  return (
    <>
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editing ? 'Editar categoría de gastos' : 'Categoría de gastos'}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {editing ? (
                <>
                  <FormControl>
                    <FormLabel>Nombre</FormLabel>
                    <TextField
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      fullWidth
                      disabled={loading}
                    />
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.hasIva}
                        onChange={(e) =>
                          setFormData({ ...formData, hasIva: e.target.checked })
                        }
                        disabled={loading}
                      />
                    }
                    label="Tiene IVA"
                  />
                </>
              ) : (
                <>
                  <KeyValueRow label="Nombre" value={category.name} />
                  <KeyValueRow
                    label="Tiene IVA"
                    value={category.hasIva ? 'Si' : 'No'}
                  />
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <CustomButton
              onClick={handleDelete}
              color="error"
              disabled={loading || editing}
            >
              {loading ? 'Eliminando...' : 'Eliminar'}
            </CustomButton>
            <div style={{ display: 'flex', gap: 10 }}>
              {editing ? (
                <>
                  <CustomButton
                    onClick={handleCancel}
                    disabled={loading}
                    color="secondary"
                  >
                    Cancelar
                  </CustomButton>
                  <CustomButton onClick={handleSave} disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar'}
                  </CustomButton>
                </>
              ) : (
                <>
                  <CustomButton
                    onClick={() => setEditing(true)}
                    color="warning"
                  >
                    Editar
                  </CustomButton>
                  <CustomButton
                    onClick={() => setOpen(false)}
                    color="secondary"
                  >
                    Salir
                  </CustomButton>
                </>
              )}
            </div>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog open={openConfirmDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          {loading ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <Typography>
              ¿Está seguro que desea eliminar la categoría &quot;
              {category?.name}&quot;? Esta acción no se puede deshacer.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenConfirmDelete(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={handleConfirmDelete}
            color="error"
            disabled={loading}
          >
            {loading ? 'Eliminando...' : 'Eliminar'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestExpensesCategorySummary;
